import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import {
  canShowInvoicePdf,
  fetchInvoicePdf,
  fetchInvoiceXrechnung,
  loadInvoicePdfBlob,
} from "@/lib/invoicePdf";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";

export interface InvoiceDocumentActionsProps {
  invoiceId: string;
  invoiceNumber: string;
  status: string;
  disabled?: boolean;
  children?: ReactNode;
}

/** PDF der Rechnung direkt in der Buchung, ohne neues Fenster. */
export function InvoiceDocumentActions({
  invoiceId,
  invoiceNumber,
  status,
  disabled = false,
  children,
}: InvoiceDocumentActionsProps) {
  const [open, setOpen] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!canShowInvoicePdf(status)) return null;

  async function showPdf() {
    if (open) {
      setOpen(false);
      return;
    }
    setError(null);
    setOpen(true);
    if (blobUrl) return;
    setLoadingPreview(true);
    try {
      const blob = await loadInvoicePdfBlob(invoiceId, { withLetterhead: true });
      const url = URL.createObjectURL(blob);
      setBlobUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return url;
      });
    } catch (e) {
      setOpen(false);
      setError(e instanceof Error ? e.message : "Rechnungsdokument konnte nicht geladen werden.");
    } finally {
      setLoadingPreview(false);
    }
  }

  async function savePdf() {
    setBusy("pdf");
    setError(null);
    try {
      await fetchInvoicePdf(invoiceId, invoiceNumber, "download");
    } catch (e) {
      setError(e instanceof Error ? e.message : "PDF konnte nicht gespeichert werden.");
    } finally {
      setBusy(null);
    }
  }

  async function saveXrechnung() {
    setBusy("xml");
    setError(null);
    try {
      await fetchInvoiceXrechnung(invoiceId, invoiceNumber);
    } catch (e) {
      setError(e instanceof Error ? e.message : "X-Rechnung konnte nicht geladen werden.");
    } finally {
      setBusy(null);
    }
  }

  const locked = disabled || !!busy || loadingPreview;

  return (
    <div className="mt-3 space-y-2">
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={loadingPreview}
          disabled={locked && !loadingPreview}
          onClick={() => void showPdf()}
        >
          {open ? "PDF schließen" : "PDF anzeigen"}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          loading={busy === "pdf"}
          disabled={locked}
          onClick={() => void savePdf()}
        >
          PDF speichern
        </Button>
        {isInvoiceFinalized(status) ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            loading={busy === "xml"}
            disabled={locked}
            onClick={() => void saveXrechnung()}
          >
            X-Rechnung speichern
          </Button>
        ) : null}
        {children}
      </div>
      {error ? <p className="text-sm text-status-red">{error}</p> : null}
      {open ? (
        <div className="overflow-hidden rounded-xl border border-separator bg-white">
          {loadingPreview && !blobUrl ? (
            <p className="px-4 py-16 text-center text-sm text-neutral-500">
              Rechnungsdokument wird geladen…
            </p>
          ) : null}
          {blobUrl ? (
            <iframe
              title={`Rechnung ${invoiceNumber}`}
              src={blobUrl}
              className="h-[min(70vh,36rem)] w-full bg-white"
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
